import { useEffect, useState, useCallback } from 'react'
import { X, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import DOMPurify from 'dompurify'
import { documentsApi, type DocumentFile } from '../api/documents'
import toast from 'react-hot-toast'

interface Props {
  files: DocumentFile[]
  initialIndex: number
  companyId: string
  leadId: string
  onClose: () => void
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentViewerModal({ files, initialIndex, companyId, leadId, onClose }: Props) {
  const [idx, setIdx] = useState(initialIndex)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const file = files[idx]

  const loadBlob = useCallback(async (f: DocumentFile) => {
    setLoading(true)
    setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null })
    try {
      const res = await documentsApi.stream(companyId, leadId, f.id)
      let blob: Blob
      if (f.mimeType === 'image/svg+xml') {
        const clean = DOMPurify.sanitize(await res.data.text(), {
          USE_PROFILES: { svg: true, svgFilters: true },
        })
        blob = new Blob([clean], { type: 'image/svg+xml' })
      } else {
        blob = res.data
      }
      setBlobUrl(URL.createObjectURL(blob))
    } catch {
      toast.error('Failed to load file')
    } finally {
      setLoading(false)
    }
  }, [companyId, leadId])

  useEffect(() => { loadBlob(file) }, [file, loadBlob])

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl) }
  }, [blobUrl])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft' && idx > 0) setIdx(i => i - 1)
      if (e.key === 'ArrowRight' && idx < files.length - 1) setIdx(i => i + 1)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [idx, files.length, onClose])

  const handleDownload = async () => {
    try {
      const res = await documentsApi.stream(companyId, leadId, file.id)
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = file.originalName
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Download failed')
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-gray-900 rounded-xl flex flex-col w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-white text-sm font-medium truncate">{file.originalName}</span>
            <span className="text-gray-500 text-xs flex-shrink-0">{formatBytes(file.sizeBytes)}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-4">
            <button onClick={handleDownload} className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-white" title="Download">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={() => setIdx(i => i - 1)} disabled={idx === 0} className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-white disabled:opacity-30" title="Previous (←)">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setIdx(i => i + 1)} disabled={idx === files.length - 1} className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-white disabled:opacity-30" title="Next (→)">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-red-400" title="Close (Esc)">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-950 flex items-center justify-center min-h-[300px]">
          {loading && <div className="text-gray-500 text-sm">Loading…</div>}
          {!loading && blobUrl && file.mimeType.startsWith('image/') && (
            <img src={blobUrl} alt={file.originalName} className="max-w-full max-h-full object-contain" />
          )}
          {!loading && blobUrl && file.mimeType === 'application/pdf' && (
            <iframe src={blobUrl} title={file.originalName} className="w-full h-full min-h-[500px] border-0" />
          )}
          {!loading && blobUrl && file.mimeType.startsWith('video/') && (
            <video src={blobUrl} controls autoPlay className="max-w-full max-h-full" />
          )}
        </div>

        <div className="px-4 py-2 border-t border-gray-700 flex items-center justify-between flex-shrink-0">
          <span className="text-gray-500 text-xs">
            {file.uploadedByUser
              ? `Uploaded by ${file.uploadedByUser.firstName} ${file.uploadedByUser.lastName}`
              : 'Uploaded'
            } · {new Date(file.createdAt).toLocaleDateString()}
          </span>
          <span className="text-gray-600 text-xs">{idx + 1} of {files.length} · ← → to navigate · Esc to close</span>
        </div>
      </div>
    </div>
  )
}
